import { useEffect, useState } from "react";
import { useSubscription, type SubscriptionTier } from "@/lib/useSubscription";

const SERIF = "'Cormorant Garamond', Georgia, serif";
const STORAGE_KEY = "caileve_last_known_tier";
const RANK: Record<SubscriptionTier, number> = { free: 0, premium: 1, atelier: 2 };

export function TierArrival({ onDone }: { onDone: () => void }) {
  const { tier, isLoading } = useSubscription();
  const [showTier, setShowTier] = useState<"premium" | "atelier" | null>(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    let prev: SubscriptionTier = "free";
    try {
      const raw = localStorage.getItem(STORAGE_KEY) as SubscriptionTier | null;
      if (raw === "free" || raw === "premium" || raw === "atelier") prev = raw;
    } catch {
      /* noop */
    }
    if ((tier === "premium" || tier === "atelier") && RANK[tier] > RANK[prev]) {
      setShowTier(tier);
      try {
        localStorage.setItem(STORAGE_KEY, tier);
      } catch {
        /* noop */
      }
      const fadeT = setTimeout(() => setFading(true), 1800);
      const doneT = setTimeout(() => {
        setShowTier(null);
        onDone();
      }, 2100);
      return () => {
        clearTimeout(fadeT);
        clearTimeout(doneT);
      };
    } else {
      try {
        localStorage.setItem(STORAGE_KEY, tier);
      } catch {
        /* noop */
      }
      onDone();
    }
  }, [tier, isLoading, onDone]);

  if (!showTier) return null;

  const isAtelier = showTier === "atelier";
  const bg = isAtelier ? "var(--caileve-ink)" : "var(--caileve-bg)";
  const titleColor = isAtelier ? "var(--caileve-bg)" : "var(--caileve-ink)";
  const lineColor = isAtelier ? "var(--caileve-gold)" : "var(--caileve-lilac)";
  const title = isAtelier ? "Welcome to Atelier." : "Welcome to Cailève Premium.";
  const subtitle = isAtelier
    ? "The full Cailève experience is yours."
    : "Your collection has no limits.";
  const philosophy = isAtelier
    ? "The piece comes first. It always has. Cailève simply remembers that."
    : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-opacity duration-300"
      style={{ backgroundColor: bg, opacity: fading ? 0 : 1 }}
    >
      <h1
        className="text-center px-6"
        style={{
          fontFamily: SERIF,
          color: titleColor,
          fontSize: "clamp(28px, 5vw, 36px)",
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </h1>
      <div
        style={{
          width: 80,
          height: 1,
          backgroundColor: lineColor,
          marginTop: 20,
          marginBottom: 20,
        }}
      />
      <p
        className="italic text-center px-6"
        style={{ fontFamily: SERIF, color: "var(--caileve-muted)", fontSize: 18 }}
      >
        {subtitle}
      </p>
      {philosophy && (
        <p
          className="italic text-center px-6 mt-4"
          style={{ fontFamily: SERIF, color: "var(--caileve-gold-text)", fontSize: 15 }}
        >
          {philosophy}
        </p>
      )}
    </div>
  );
}